<template>
    <div class="full-height bg-white">
        <div
            class="row justify-between items-center bg-primary q-px-md q-py-sm"
        >
            <div>
                <q-btn-group>
                    <q-btn
                        @click="isTableMode = true"
                        :color="isTableMode ? 'primary' : 'white'"
                        :text-color="isTableMode ? 'white' : 'primary'"
                        class="toggle-report-view-button"
                        icon="fas fa-table"
                        size="10px"
                        :outline="!isTableMode"
                    >
                        <q-tooltip>
                            {{ $t('Switch to table view') }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        @click="isTableMode = false"
                        :color="isTableMode ? 'white' : 'primary'"
                        :text-color="isTableMode ? 'primary' : 'white'"
                        class="toggle-report-view-button"
                        icon="fas fa-chart-bar"
                        size="10px"
                        :outline="isTableMode"
                    >
                        <q-tooltip>
                            {{ $t('Switch to chart view') }}
                        </q-tooltip>
                    </q-btn>
                </q-btn-group>
            </div>

            <div class="float-right">
                <q-btn-group flat v-if="!$q.platform.is.mobile">
                    <q-btn
                        id="download-csv-button"
                        @click="exportDataAsCSV"
                        :loading="isDownloadingCSV"
                        class="download-file-buttons"
                        icon="far fa-file-csv"
                        text-color="white"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (CSV)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        id="download-xls-button"
                        @click="exportDataAsXLS"
                        :loading="isDownloadingXLS"
                        text-color="white"
                        class="download-file-buttons"
                        icon="far fa-file-excel"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (XLS)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                </q-btn-group>
            </div>
        </div>

        <!-- Table mode -->
        <quality-report-table
            v-if="isTableMode"
            class="q-mb-md"
            :report="report"
            :facilityId="facilityId"
            :selectedGroupBy="selectedGroupBy"
            @setOrder="setOrder"
            :label="
                $t('Current Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(report.dateInterval.start),
                    endDate: this.formatDate(report.dateInterval.end)
                })
            "
            @handleShowEvents="handleShowEvents"
        />
        <quality-report-table
            v-if="isTableMode"
            :report="comparisonReport"
            :facilityId="facilityId"
            :selectedGroupBy="selectedGroupBy"
            @setOrder="setOrder"
            :label="
                $t('Comparison Period: {startDate} - {endDate}', {
                    startDate: this.formatDate(
                        comparisonReport.dateInterval.start
                    ),
                    endDate: this.formatDate(comparisonReport.dateInterval.end)
                })
            "
            @handleShowEvents="handleShowEvents"
        />

        <!-- Dashboard mode -->
        <div v-else class="report-container">
            <quality-dashboard
                :report="report"
                :comparisonReport="comparisonReport"
                :facility="facilityId"
                :selectedGroupBy="selectedGroupBy"
            ></quality-dashboard>
        </div>

        <!-- View events modal -->
        <q-dialog v-model="showEventsModal">
            <quality-events-table
                @close="showEventsModal = false"
                :filters="eventsFilters"
            ></quality-events-table>
        </q-dialog>
    </div>
</template>

<script>
import QualityEventsTable from '@components/QualityEventsTable.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { formatNumber } from '@utils/numberFormatters.js';
import { mapGetters } from 'vuex';
import QualityDashboard from './QualityDashboard.vue';
import {
    buildQualityReport,
    buildTable,
    NON_QUALITY_EVENT_DEFECT_NAME,
    UNJUSTIFIED_DEFECT_NAME
} from './qualityReport.js';
import QualityReportTable from './QualityReportTable.vue';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ComparisonQualityReport',
    components: { QualityReportTable, QualityDashboard, QualityEventsTable },
    props: {
        records: {
            type: Object,
            required: true
        },
        facilityId: {
            type: Number,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        selectedShifts: {
            type: Set,
            required: true
        },
        selectedGroupBy: {
            type: String,
            required: true
        },
        shiftIdToNames: {
            type: Object,
            required: true
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    data() {
        return {
            orderBy: 'rejectedProduction',
            descending: true,

            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // report/table view toggler
            isTableMode: true,

            // file exporter class
            fileExporter: null,

            showEventsModal: false
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            const reportData = buildQualityReport(
                this.records.currentReportData,
                this.filters,
                this.selectedGroupBy
            );
            const tableData = buildTable(
                reportData,
                this.selectedGroupBy,
                this.orderBy,
                this.descending
            );
            const report = {
                ...tableData,
                dateInterval: {
                    start: this.getDateRange.start,
                    end: this.getDateRange.end
                }
            };
            return report;
        },
        /**
         * Build report data of comparison interval
         * @return {Object} report data of comparison interval
         */
        comparisonReport() {
            const reportData = buildQualityReport(
                this.records.comparisonReportData,
                this.filters,
                this.selectedGroupBy
            );
            const tableData = buildTable(
                reportData,
                this.selectedGroupBy,
                this.orderBy,
                this.descending
            );
            const report = {
                ...tableData,
                dateInterval: {
                    start: this.getDateRange.compareStart,
                    end: this.getDateRange.compareEnd
                }
            };
            return report;
        },
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for exporting
         */
        fileHeaders() {
            return [
                this.$t('Defect'),
                this.$t('Asset'),
                this.$t('Product'),
                this.$t('SKU'),
                this.$t('Shift'),
                this.$t('Number of Rejected Products'),
                this.$t('Number of Quality Events'),
                this.$t('Report Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Report End Date').concat(` [${this.localTimezone}]`)
            ];
        },
        /**
         * Get the file rows for file exporting
         * @return {Array} merged file rows (current & comparison) for file exporting
         */
        fileRows() {
            const { currentReportData, comparisonReportData } = this.records;
            const currentReportRows = this.getFileRows(
                currentReportData,
                this.getDateRange.start,
                this.getDateRange.end
            );
            const comparisonReportRows = this.getFileRows(
                comparisonReportData,
                this.getDateRange.compareStart,
                this.getDateRange.compareEnd
            );

            return [...currentReportRows, ...comparisonReportRows];
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets,
                shiftIds: this.selectedShifts
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId)?.name;
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        },
        /**
         * Get the unit system used by the facility
         * @return {String} unit system
         */
        unitSystem() {
            return this.getFacility(this.facilityId)?.unit_system;
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Handles ordering of report table
         * Note: Use Object for named parameters to avoid linting errors
         * @param {String} orderBy - column name to order by
         * @param {Boolean} descending - order direction
         */
        setOrder({ orderBy, descending }) {
            this.orderBy = orderBy;
            this.descending = descending;
        },
        /**
         * Extract all file rows for a given report data
         * @param {Object} report - report data to extract rows from
         * @param {String} dateStart - start date of the report
         * @param {String} dateEnd - end date of the report
         * @return {Array} 2D array containing file row data
         */
        getFileRows(report, dateStart, dateEnd) {
            let rows = [];

            const filteredRecords = report.filter(record => {
                return (
                    this.filters.assetIds.has(record['asset_id']) &&
                    this.filters.shiftIds.has(record['shift_id'])
                );
            });

            for (const record of filteredRecords) {
                // rename null defects
                let defectName = this.getDefectName(record);
                // only include assets that are found in the report
                const asset = this.getAsset(record['asset_id']);

                if (asset) {
                    let row = [
                        defectName,
                        !asset.is_deleted
                            ? asset.name
                            : // append (Deleted) to asset name if asset is deleted
                              this.$t('{assetName} (Deleted)', {
                                  assetName: asset.name
                              }),
                        record['product_name'],
                        record['sku'],
                        this.shiftIdToNames[record['shift_id']],
                        formatNumber(
                            parseFloat(record['rejected_production'] ?? 0)
                        ),
                        record['quality_event_count'],
                        this.formatDate(dateStart),
                        this.formatDate(dateEnd)
                    ];
                    rows.push(row);
                }
            }

            return rows;
        },
        /**
         * Handle show events dialog.
         * Note: Use Object for named parameters to avoid linting errors
         * @param {Object} row row data of the clicked row
         * @param {Object} dateInterval date interval of the clicked row
         */
        handleShowEvents({ row, dateInterval }) {
            // set URL query values to be used by event dialog
            this.eventsFilters = {
                groupBy: this.selectedGroupBy,
                defectId: row.defectId,
                defectName: row.defectName,
                assetId:
                    this.selectedGroupBy === 'assets' ? row.id : row.assetId, // Include asset ID in products grouping for retrieving defects when justifying
                assetName: this.selectedGroupBy === 'assets' ? row.name : null,
                productId: this.selectedGroupBy === 'products' ? row.id : null,
                productName:
                    this.selectedGroupBy === 'products' ? row.name : null,
                start: dateInterval.start,
                end: dateInterval.end
            };
            // show events dialog
            this.showEventsModal = true;
        },
        /**
         * Export raw records as CSV.
         */
        exportDataAsCSV() {
            // begin loading
            this.isDownloadingCSV = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            const compare_start_date = this.formatDate(
                this.getDateRange.compareStart
            );
            const compare_end_date = this.formatDate(
                this.getDateRange.compareEnd
            );
            try {
                this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_quality_${start_date}_${end_date}_and_${compare_start_date}_${compare_end_date}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                console.error(error);
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS() {
            // begin loading
            this.isDownloadingXLS = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            const compare_start_date = this.formatDate(
                this.getDateRange.compareStart
            );
            const compare_end_date = this.formatDate(
                this.getDateRange.compareEnd
            );
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_quality_${start_date}_${end_date}_and_${compare_start_date}_${compare_end_date}`,
                    sheetName: this.$t('Quality Report'),
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        },
        /**
         * Extract defect type
         * @param {Object} record record data
         */
        getDefectName(record) {
            if (record.defect_id === null) {
                return record.quality_event_count > 0
                    ? UNJUSTIFIED_DEFECT_NAME
                    : NON_QUALITY_EVENT_DEFECT_NAME;
            } else {
                return record.defect_name;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$table-height: 100%;
$report-header-panel-height: 50px;

.toggle-report-view-button :deep(.q-btn__wrapper) {
    padding: 6px;
}

:deep(.report-container) {
    border: 1px solid grey;
    height: calc(#{$table-height} - #{$report-header-panel-height});
}

.report-panel-header {
    height: $report-header-panel-height;
}
</style>
